import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Calendar, Wallet, UserCircle, LogOut,
  Power, Coffee, Star, ShieldCheck, IndianRupee, CalendarClock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { PushAlertsToggle } from "@lawyer/components/PushAlertsToggle";
import { MobileHeader, MobileDrawer } from "@lawyer/components/mobile/MobileNav";
import { LAWYER_BASE, LAWYER_LOGIN_PATH } from "@lawyer/constants/route";

const NAV = [
  { to: "", label: "Dashboard", icon: LayoutDashboard, exact: true },
  { to: "consultations", label: "Consultations", icon: Calendar },
  { to: "availability", label: "Availability", icon: CalendarClock },
  { to: "earnings", label: "Earnings", icon: Wallet },
  { to: "pricing", label: "Pricing", icon: IndianRupee },
  { to: "reviews", label: "Reviews", icon: Star },
  { to: "kyc", label: "Verification", icon: ShieldCheck },
  { to: "profile", label: "Profile", icon: UserCircle },
];

function SidebarItem({ to, label, icon: Icon, exact }) {
  return (
    <NavLink
      to={`${LAWYER_BASE}/${to}`}
      end={exact}
      onClick={(e) => {
        // Availability page flags unsaved edits on <body>; confirm before leaving them behind.
        if ((document.body.dataset.unsaved === "1" || document.body.dataset.unsavedOverride === "1")
          && !window.confirm("You have unsaved availability changes. Leave without saving?")) e.preventDefault();
      }}
      data-testid={`lawyer-nav-${label.toLowerCase()}`}
      className={({ isActive }) =>
        `flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all ${
          isActive
            ? "bg-[#D4AF37] text-[#0A2540] shadow"
            : "text-slate-300 hover:bg-white/10 hover:text-white"
        }`
      }
    >
      <Icon size={18} />
      <span>{label}</span>
    </NavLink>
  );
}

// `listed === null` → dashboard data not loaded yet: show a neutral placeholder, never the online/vacation controls.
const STATE_LABEL = {
  vacation: "On vacation",
  busy: "Busy — in a session",
  online_scheduled: "Online — scheduled hours",
  online_manual: "Online — accepting bookings",
  offline: "Offline",
};
const minutesLeft = (iso) => Math.max(0, Math.round((new Date(iso) - Date.now()) / 60000));

export default function LawyerLayout({ children, available, vacationMode, listed = true, availability = null, vacationUntil = null, alertCount = 0, photo = null, onAvailabilityToggle, onVacationToggle }) {
  const statusLoading = listed === null || listed === undefined;
  // Effective state comes from the availability engine; the boolean props are the legacy fallback.
  const state = availability?.state || (vacationMode ? "vacation" : available ? "online_manual" : "offline");
  const online = state === "online_scheduled" || state === "online_manual";
  const busy = state === "busy";
  const fmtIst = (iso) => new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", timeZone: "Asia/Kolkata" });
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);

  const initials = (user?.name || user?.email || "L").split(" ").slice(-2).map((x) => x[0]).join("").toUpperCase();
  const handleLogout = async () => { await logout(); navigate(LAWYER_LOGIN_PATH); };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Phone: compact top bar + hamburger drawer. Desktop (lg+): the persistent sidebar below, unchanged. */}
      <MobileDrawer open={open} onClose={() => setOpen(false)} nav={NAV} listed={statusLoading ? null : listed} state={state}
        availability={availability} vacationUntil={vacationUntil} user={user} initials={initials} onLogout={handleLogout} />

      <aside
        data-testid="lawyer-sidebar"
        className="hidden lg:flex sticky top-0 left-0 h-screen w-64 bg-[#0A2540] text-white p-4 z-40 flex-col"
      >
        <div className="flex items-center gap-2 px-2 py-2 mb-6">
          <div className="h-9 w-9 rounded-lg bg-[#D4AF37] grid place-items-center text-[#0A2540] font-extrabold">A</div>
          <div className="leading-tight">
            <p className="font-extrabold tracking-tight" style={{ fontFamily: "Poppins" }}>Advoset Lawyer</p>
            <p className="text-[10px] uppercase tracking-widest text-[#D4AF37]">Legal Help, Simplified</p>
          </div>
        </div>

        {/* Status pill in the sidebar — availability only exists once the profile is listed */}
        {statusLoading ? (
          <div className="px-2 mb-3" data-testid="lawyer-status-card-loading">
            <div className="rounded-xl border border-white/10 bg-white/5 p-3 animate-pulse">
              <p className="text-[10px] uppercase tracking-widest font-bold text-slate-300">Status</p>
              <div className="mt-2 h-3 w-24 rounded bg-white/10" />
              <div className="mt-3 h-7 w-full rounded-full bg-white/10" />
            </div>
          </div>
        ) : !listed ? (
          <div className="px-2 mb-3" data-testid="lawyer-status-card-unlisted">
            <div className="rounded-xl border border-white/10 bg-white/5 p-3">
              <p className="text-[10px] uppercase tracking-widest font-bold text-slate-300">Status</p>
              <p className="mt-0.5 text-sm font-extrabold text-slate-300">Not listed yet</p>
              <p className="mt-1 text-[11px] leading-snug text-slate-400">
                Online/offline and vacation controls unlock once your profile is verified and listed. Track progress on the Dashboard.
              </p>
            </div>
          </div>
        ) : (
        <div className="px-2 mb-3" data-testid="lawyer-status-card">
          <div className={`rounded-xl border p-3 ${state === "vacation" ? "border-amber-300 bg-amber-500/10" : online ? "border-emerald-400 bg-emerald-500/10" : busy ? "border-amber-300/60 bg-amber-500/5" : "border-white/10 bg-white/5"}`}>
            <p className="text-[10px] uppercase tracking-widest font-bold text-slate-300">Status</p>
            <p className={`mt-0.5 text-sm font-extrabold ${state === "vacation" || busy ? "text-amber-300" : online ? "text-emerald-300" : "text-slate-300"}`} data-testid="lawyer-status-label" data-state={state}>
              {STATE_LABEL[state] || "Offline"}
            </p>
            {state === "online_scheduled" && availability?.scheduled_until && (
              <p className="text-[10px] text-slate-300 mt-0.5" data-testid="lawyer-status-detail">Instant consults on until {fmtIst(availability.scheduled_until)} IST</p>
            )}
            {state === "online_manual" && availability?.manual_online_until && (
              <p className="text-[10px] text-slate-300 mt-0.5" data-testid="lawyer-status-detail">{minutesLeft(availability.manual_online_until)} min left · activity keeps you online</p>
            )}
            {state === "busy" && <p className="text-[10px] text-slate-300 mt-0.5" data-testid="lawyer-status-detail">No new instant consults until this one ends</p>}
            {state === "offline" && <p className="text-[10px] text-slate-300 mt-0.5" data-testid="lawyer-status-detail">Switch on for 30 min, or wait for your scheduled hours</p>}
            <div className="mt-2 flex gap-2">
              <button
                onClick={onAvailabilityToggle}
                disabled={state === "vacation" || state === "online_scheduled"}
                data-testid="toggle-online"
                className={`flex-1 text-[11px] font-bold px-2 py-1 rounded-md border ${state === "online_manual" ? "border-rose-300 text-rose-200" : "border-emerald-300 text-emerald-200"} disabled:opacity-40`}
              >
                <Power size={10} className="inline mr-1" />
                {state === "online_manual" ? "Go offline" : "Go online"}
              </button>
              <button
                onClick={onVacationToggle}
                data-testid="toggle-vacation"
                className={`flex-1 text-[11px] font-bold px-2 py-1 rounded-md border ${state === "vacation" ? "border-amber-300 text-amber-200" : "border-white/20 text-slate-200"}`}
              >
                <Coffee size={10} className="inline mr-1" />
                {state === "vacation" ? "End break" : "Vacation"}
              </button>
            </div>
            {state === "online_manual" && (
              <button onClick={onAvailabilityToggle} data-testid="stay-online" data-action="extend"
                className="mt-2 w-full text-[11px] font-bold px-2 py-1 rounded-md border border-emerald-300/60 text-emerald-200 hover:bg-emerald-500/10">
                Stay online · +30 min
              </button>
            )}
            <PushAlertsToggle />
          </div>
        </div>
        )}

        <nav className="space-y-1 flex-1">
          {NAV.map((item) => <SidebarItem key={item.label} {...item} />)}
        </nav>

        <div className="mt-3 bg-white/5 border border-white/10 rounded-xl p-3 flex items-center gap-3">
          <div className="h-9 w-9 rounded-lg bg-[#D4AF37] text-[#0A2540] grid place-items-center font-extrabold text-sm">
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold truncate" data-testid="lawyer-current-user-name">{user?.name || user?.email}</p>
            <p className="text-[10px] text-slate-400 truncate">Lawyer</p>
          </div>
          <button onClick={handleLogout} className="text-slate-300 hover:text-white" title="Sign out" data-testid="lawyer-logout-btn">
            <LogOut size={16} />
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <MobileHeader onMenu={() => setOpen(true)} alertCount={alertCount} initials={initials} photo={photo} />
        <div className="p-4 sm:p-6 lg:p-8 max-w-[1500px] mx-auto" data-testid="lawyer-main">
          {children}
        </div>
      </main>
    </div>
  );
}
